import axios from "axios";
import { doneProgress, startProgress } from "../Global/ProgressBar";
import { getRole, getRolePermissions, MODULE_KEYS, normalizeRoleScope, resolveRoleIdFromValue } from "../config/rbacConfig";
import { installOfflineInterceptors } from "../offline/offlineInterceptors";
import { rewriteRequestConfigWithUuidAliases } from "../offline/db";
import { extractScopeSeed } from "../utils/scopeFields";

// Use the local `/api` proxy on localhost so CRA/dev-server requests stay
// same-origin. On other hosts we fall back to the configured backend origin.
const DEFAULT_BACKEND_URL = "https://nlimsbe.olimageserver.com";

function normalizeBackendUrl(value) {
  const text = String(value ?? "").trim();
  if (!text) return DEFAULT_BACKEND_URL;
  return text.replace(/\/+$/, "");
}

function isLocalhostHost(hostname) {
  return hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1";
}

const BACKEND_URL = normalizeBackendUrl(process.env.REACT_APP_BACKEND_URL);
const USE_LOCAL_PROXY =
  typeof window !== "undefined" && isLocalhostHost(window.location.hostname);

const authClient = axios.create({
  baseURL: USE_LOCAL_PROXY ? "" : BACKEND_URL,
  headers: { "Content-Type": "application/json" },
});

const client = axios.create({
  baseURL: USE_LOCAL_PROXY ? "/api" : `${BACKEND_URL}/api`,
  headers: { "Content-Type": "application/json" },
});

let accessToken = null;

export function setAccessToken(token) {
  accessToken = token;
}

function wireProgress(instance) {
  instance.interceptors.request.use(
    (config) => {
      startProgress();
      if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`;
      return config;
    },
    (error) => {
      doneProgress();
      return Promise.reject(error);
    }
  );

  instance.interceptors.response.use(
    (response) => {
      doneProgress();
      return response;
    },
    (error) => {
      doneProgress();
      return Promise.reject(error);
    }
  );
}

function normalizeCollectionResponse(response) {
  const method = String(response?.config?.method ?? '').toLowerCase();
  if (method !== 'get') return response;

  const payload = response?.data;
  if (Array.isArray(payload)) {
    response.data = {
      data: { items: payload, pagination: null },
      items: payload,
      results: payload,
    };
    return response;
  }

  if (!payload || typeof payload !== 'object') return response;

  const next = { ...payload };
  const rawItems =
    Array.isArray(next.data) ? next.data :
    Array.isArray(next.items) ? next.items :
    Array.isArray(next.results) ? next.results :
    Array.isArray(next.data?.items) ? next.data.items :
    Array.isArray(next.data?.results) ? next.data.results :
    null;

  if (!rawItems) return response;

  const pagination =
    next.pagination ??
    next.data?.pagination ??
    null;

  next.items = Array.isArray(next.items) ? next.items : rawItems;
  next.results = Array.isArray(next.results) ? next.results : rawItems;
  next.data = {
    ...(next.data && typeof next.data === 'object' && !Array.isArray(next.data) ? next.data : {}),
    items: rawItems,
    pagination,
  };

  return response;
}

[client, authClient].forEach(wireProgress);

client.interceptors.response.use((response) => normalizeCollectionResponse(response));
installOfflineInterceptors(client);
client.interceptors.request.use((config) => rewriteRequestConfigWithUuidAliases(config));

function resolveRoleIdFromUser(user = {}) {
  const candidates = [
    user.role_id,
    user.roleId,
    user.role,
    user.roles?.[0],
    user.role?.id,
    user.role?.role_id,
    user.role?.roleId,
    user.role?.code,
    user.role?.backend_class,
    user.role?.backendClass,
    user.role?.name,
    user.role?.label,
    user.roles?.[0]?.id,
    user.roles?.[0]?.role_id,
    user.roles?.[0]?.roleId,
    user.roles?.[0]?.code,
    user.roles?.[0]?.backend_class,
    user.roles?.[0]?.backendClass,
    user.roles?.[0]?.name,
    user.roles?.[0]?.label,
    user.role_code,
    user.roleCode,
    user.role_name,
    user.roleName,
    user.role_label,
    user.roleLabel,
    user.backend_class,
    user.backendClass,
  ];

  for (const candidate of candidates) {
    const resolved = resolveRoleIdFromValue(candidate);
    if (resolved) return resolved;
  }

  return null;
}

const MODULE_SCOPE_MAP = {
  mod_1: MODULE_KEYS.POPULATION,
  mod_2: MODULE_KEYS.MARKETS,
  mod_3: MODULE_KEYS.INFRASTRUCTURE,
  mod_4: MODULE_KEYS.WORKFORCE,
  mod_5: MODULE_KEYS.PLANNING,
  mod_6: MODULE_KEYS.CONFLICTS,
  mod_7: MODULE_KEYS.ANIMAL_IDENTIFICATION,
  mod_8: MODULE_KEYS.ANIMAL_HEALTH,
};

function normalizeModuleKey(value) {
  const text = String(value ?? "").trim();
  if (!text) return null;
  if (Object.values(MODULE_KEYS).includes(text)) return text;
  if (MODULE_SCOPE_MAP[text]) return MODULE_SCOPE_MAP[text];
  if (MODULE_SCOPE_MAP[text.toLowerCase()]) return MODULE_SCOPE_MAP[text.toLowerCase()];
  const compact = text.toLowerCase().replace(/[^a-z0-9]+/g, "");
  if (!compact) return null;

  if (
    compact === "1" ||
    compact === "mod1" ||
    compact === "module1" ||
    compact.includes("livestockpopulation") ||
    compact.includes("populationrecords") ||
    compact === "population"
  ) {
    return MODULE_KEYS.POPULATION;
  }

  if (
    compact === "2" ||
    compact === "mod2" ||
    compact === "module2" ||
    compact.includes("marketstrade") ||
    compact === "markets"
  ) {
    return MODULE_KEYS.MARKETS;
  }

  if (
    compact === "3" ||
    compact === "mod3" ||
    compact === "module3" ||
    compact.includes("infrastructure") ||
    compact.includes("services")
  ) {
    return MODULE_KEYS.INFRASTRUCTURE;
  }

  if (
    compact === "4" ||
    compact === "mod4" ||
    compact === "module4" ||
    compact.includes("workforce") ||
    compact.includes("institutional") ||
    compact.includes("actor")
  ) {
    return MODULE_KEYS.WORKFORCE;
  }

  if (
    compact === "5" ||
    compact === "mod5" ||
    compact === "module5" ||
    compact.includes("planning")
  ) {
    return MODULE_KEYS.PLANNING;
  }

  if (
    compact === "6" ||
    compact === "mod6" ||
    compact === "module6" ||
    compact.includes("conflict") ||
    compact.includes("dispute")
  ) {
    return MODULE_KEYS.CONFLICTS;
  }

  if (
    compact === "7" ||
    compact === "mod7" ||
    compact === "module7" ||
    compact.includes("animalidentification")
  ) {
    return MODULE_KEYS.ANIMAL_IDENTIFICATION;
  }

  if (
    compact === "8" ||
    compact === "mod8" ||
    compact === "module8" ||
    compact.includes("animalhealth")
  ) {
    return MODULE_KEYS.ANIMAL_HEALTH;
  }

  return null;
}

function extractAssignedModules(user = {}) {
  const candidates = [];

  const pushTokens = (value) => {
    if (value === null || value === undefined) return;
    if (Array.isArray(value)) {
      value.forEach((entry) => pushTokens(entry));
      return;
    }
    if (typeof value === "object") {
      pushTokens(
        value.module_scope ??
        value.moduleScope ??
        value.module ??
        value.moduleKey ??
        value.module_key ??
        value.module_code ??
        value.moduleCode ??
        value.module_id ??
        value.moduleId ??
        value.code ??
        value.slug ??
        value.name ??
        value.label
      );
      return;
    }

    String(value)
      .split(/[,\s|;]+/)
      .forEach((token) => {
        if (token) candidates.push(token);
      });
  };

  pushTokens(user.assignedModules);
  pushTokens(user.assigned_modules);
  pushTokens(user.module_scope);
  pushTokens(user.moduleScope);
  pushTokens(user.module_assignments);
  pushTokens(user.moduleAssignments);
  pushTokens(user.role_assignment);
  pushTokens(user.roleAssignment);
  pushTokens(user.role_assignments);
  pushTokens(user.roleAssignments);
  pushTokens(user.assignment);
  pushTokens(user.assignments);

  const roles = Array.isArray(user.roles) ? user.roles : [];
  roles.forEach((role) => {
    pushTokens(role?.module_scope);
    pushTokens(role?.moduleScope);
    pushTokens(role?.module_assignments);
    pushTokens(role?.moduleAssignments);
    pushTokens(role?.role_assignment);
    pushTokens(role?.roleAssignment);
    pushTokens(role?.assignments);
  });

  return [...new Set(candidates.map(normalizeModuleKey).filter(Boolean))];
}

export const authApi = {
  // POST /api/v1/auth/login  →  { email, password }
  async login({ email, password }) {
    const { data } = await authClient.post("/api/v1/auth/login", { email, password });

    const payload = data?.data ?? {};
    const { user = {}, token, expires_at } = payload;

    if (!token || typeof token !== "string") {
      throw new Error("Invalid server response — no auth token received.");
    }

    const roleId      = resolveRoleIdFromUser(user);

    // Diagnostic: log the raw backend user object so developers can verify
    // what role data the backend is actually returning for this account.
    console.group('[NLIMS Auth] Login response for:', user.email ?? email);
    console.log('raw user object:', JSON.parse(JSON.stringify(user)));
    console.log('roles array:', user.roles ?? []);
    console.log('resolved roleId:', roleId);
    if (!roleId) {
      console.warn(
        '[NLIMS Auth] Could not resolve a roleId — the account has no role assigned in the backend.',
        '\nExpected one of: nlims_system_administrator, national_data_steward, domain_data_steward,',
        'sub_national_validator, data_originator, analytics_consumer',
        '\nAsk the backend administrator to POST /api/v1/role-assignments for this user.',
      );
    }
    console.groupEnd();

    const roleCode    =
      user.role_code ??
      user.roleCode ??
      user.role?.code ??
      user.roles?.[0]?.code ??
      user.backend_class ??
      user.backendClass ??
      roleId ??
      null;
    const role        = getRole(roleId);
    const rawScope    = extractScopeSeed(user);
    const scope       = normalizeRoleScope(roleId, rawScope);
    const permissions = Array.isArray(payload.permissions) && payload.permissions.length > 0
      ? payload.permissions
      : (roleId === "R01" ? ["*"] : getRolePermissions(roleId));
    const assignedModules = extractAssignedModules(user);
    const moduleScope =
      user.module_scope ??
      user.moduleScope ??
      user.roles?.[0]?.module_scope ??
      user.roles?.[0]?.moduleScope ??
      null;

    return {
      accessToken: token,
      expiresAt:   expires_at,
      user: {
        id:              user.uuid,
        uuid:            user.uuid,
        name:            user.name            ?? "NLIMS User",
        email:           user.email           ?? "",
        institution:     user.institution?.name ?? user.institution_name ?? null,
        institutionUuid: user.institution?.uuid ?? user.institution_uuid ?? null,
        institutionData: user.institution ?? null,
        roleId,
        roleCode,
        roleName:        role?.label          ?? roleCode ?? null,
        backendClass:    user.backend_class ?? user.backendClass ?? role?.backendClass ?? roleCode,
        isActive:        user.is_active       ?? true,
        state:           scope.state          ?? null,
        lga:             scope.lga            ?? null,
        ward:            scope.ward           ?? null,
        scope,
        scopeLevel:      scope.level          ?? null,
        assignedState:   scope.state          ?? null,
        assignedLga:     scope.lga            ?? null,
        assignedWard:    scope.ward           ?? null,
        assignedModules,
        assigned_modules: assignedModules,
        module_scope:    moduleScope,
        moduleScope,
        module_assignments: user.module_assignments ?? user.moduleAssignments ?? user.role?.module_assignments ?? user.role?.moduleAssignments ?? user.roles?.[0]?.module_assignments ?? user.roles?.[0]?.moduleAssignments ?? null,
        moduleAssignments: user.moduleAssignments ?? user.module_assignments ?? user.role?.moduleAssignments ?? user.role?.module_assignments ?? user.roles?.[0]?.moduleAssignments ?? user.roles?.[0]?.module_assignments ?? null,
        role_assignments: user.role_assignments ?? user.roleAssignments ?? user.roles?.[0]?.role_assignments ?? user.roles?.[0]?.roleAssignments ?? null,
        roleAssignments: user.roleAssignments ?? user.role_assignments ?? user.roles?.[0]?.roleAssignments ?? user.roles?.[0]?.role_assignments ?? null,
        role_assignment: user.role_assignment ?? user.roleAssignment ?? null,
        roleAssignment: user.roleAssignment ?? user.role_assignment ?? null,
        assignments: user.assignments ?? null,
      },
      permissions,
    };
  },

  async logout() {
    try {
      await authClient.post("/api/v1/auth/logout");
    } catch {
      // best-effort — clear token regardless
    }
    setAccessToken(null);
  },
};

// Real backend is the default. Set REACT_APP_USE_MOCK_API=true only when
// you explicitly want the local mock facade.
export const USE_MOCK_API = process.env.REACT_APP_USE_MOCK_API === "true";
export default client;
